<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useStore } from "vuex";

import { httpClientOmni } from "@/libraries";
import { useMutation, useQueryClient } from "@tanstack/vue-query";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/v4/popover";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/v4/tabs";
import { Button } from "@/components/v4/button";
import { ScrollArea } from "@/components/v4/scroll-area";
import { IconBell, IconPointFilled } from "@tabler/icons-vue";
import NotificationList from "./NotificationList";

const selectedTab = ref("export");

const emit = defineEmits(["update:modelValue"]);

const store = useStore();

const isLoading = ref({
  notification: false,
  export: false,
});

onMounted(() => {
  isLoading.value.notification = true;
  store
    .dispatch("app/getNotif", {
      user_id: store.getters.localStorage.decodedUserId,
      per_page: 10,
    })
    .finally(() => {
      isLoading.value.notification = false;
    });

  isLoading.value.export = true;
  store.dispatch("notification/getNotifExport").finally(() => {
    isLoading.value.export = false;
  });
});

const data = computed(() => {
  return {
    notification: store.getters["app/notifList"].data,
    export: store.getters["notification/notifExport"],
  };
});

const totalUnread = computed(() => {
  const totalExport =
    store.getters["notification/notifExport"]?.filter(
      (item) => item.seen === 0
    ).length || 0;
  const totalApp = store.getters["app/unread_notif_count"] || 0;
  return totalExport + totalApp;
});

const hasNotification = computed(() => {
  return totalUnread.value > 0;
});

watch(totalUnread, (val) => {
  if (val > 0) {
    emit("update:modelValue", true);
  } else {
    emit("update:modelValue", false);
  }
});

const mutationReadAll = useMutation({
  mutationFn: () =>
    httpClientOmni.get("notification/read-all-notif", {
      params: {
        user_id: store.getters.localStorage.decodedUserId,
      },
    }),
  onSuccess: () => {
    store.dispatch("app/getNotif", {
      user_id: store.getters.localStorage.decodedUserId,
      per_page: 10,
    });
    store.dispatch("notification/getNotifExport");
  },
});

const handleReadAll = () => {
  mutationReadAll.mutate();
};
</script>

<template>
  <Popover>
    <PopoverTrigger>
      <span class="vf-relative">
        <IconBell color="var(--neutral-400)" size="20" />
        <IconPointFilled
          color="var(--danger-400)"
          size="13"
          class="vf-absolute vf-right-0 -vf-top-px"
          v-if="hasNotification"
        />
      </span>
    </PopoverTrigger>
    <PopoverContent
      class="vf-w-[500px] !vf-rounded-lg !vf-px-4 !vf-py-2 vf-font-inter !vf-pb-3"
    >
      <div class="vf-flex vf-items-center vf-justify-between vf-mb-2">
        <h6 class="vf-text-base">Notifications</h6>
        <Button
          variant="ghost"
          class="vf-text-xs !vf-p-0"
          @click="handleReadAll"
          :disabled="mutationReadAll.isPending.value"
        >
          <span class="vf-text-neutral-400 !vf-p-0">
            {{
              mutationReadAll.isPending.value
                ? "Marking as read..."
                : "Mark all as read"
            }}
          </span>
        </Button>
      </div>
      <Tabs
        :model-value="selectedTab"
        @update:modelValue="selectedTab = $event"
      >
        <TabsList class="grid w-full grid-cols-2 !vf-justify-start">
          <!-- <TabsTrigger value="notification"> Notification </TabsTrigger> -->
          <TabsTrigger value="export"> Export </TabsTrigger>
        </TabsList>
        <TabsContent value="notification" class="vf-pt-2 vf-w-full">
          <ScrollArea class="vf-max-h-[500px] vf-overflow-y-auto">
            <NotificationList
              :data="data.notification"
              :isLoading="isLoading.notification"
              type="notification"
            />
          </ScrollArea>
        </TabsContent>
        <TabsContent value="export" class="vf-pt-2 vf-w-full">
          <ScrollArea class="vf-max-h-[500px] vf-overflow-y-auto">
            <NotificationList
              :data="data.export"
              :isLoading="isLoading.export"
              type="export"
            />
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </PopoverContent>
  </Popover>
</template>
